import { Injectable } from '@angular/core';

import { Subject, first } from 'rxjs';

import { TwcPopupRef, TwcPopupService, TwcPopupTypes } from '@topcon/twc-angular';

import { MessageDialogComponent } from './message-dialog.component';

export interface MessageDialogOptionsButton {
    id: string;
    text: string;
    primary: boolean;
    isAdditionalCheckboxSelected?: boolean;
}

export type MessageDialogOptionsWidth = 's' | 'm' | 'l' | 'xl';

export interface MessageDialogOptions {
    messageType?: MessageDialogType;
    style?: TwcPopupTypes;
    headerText: string;
    contentHeaderText?: string;
    contentText?: string;
    contentSecondaryText?: string;
    buttonsList?: MessageDialogOptionsButton[];
    width?: MessageDialogOptionsWidth;
    additionalCheckboxLabel?: string;
}

export type MessageDialogType = 'information' | 'warning' | 'error';

/**
 * The MessageDialogService is responsible to show default modal dialogs (information, warning and error) \
 */
@Injectable({ providedIn: 'root' })
export class MessageDialogService {
    private buttonClicked$ = new Subject<MessageDialogOptionsButton>();

    private defaultOptions: MessageDialogOptions = {
        style: 'primary',
        headerText: '',
        contentHeaderText: '',
        contentText: '',
        buttonsList: [this.getOkButtonConfig()],
    };

    constructor(private popupService: TwcPopupService) {}

    buttonClick(button: MessageDialogOptionsButton): void {
        this.buttonClicked$.next(button);
    }

    /** Opens an information modal dialog */
    showInformation(options: Partial<MessageDialogOptions>): Promise<MessageDialogOptionsButton> {
        const mixedOptions = { ...options };
        mixedOptions.messageType = 'information';

        return this.showDialog(mixedOptions);
    }

    /** Opens a warning modal dialog */
    showWarning(options: Partial<MessageDialogOptions>): Promise<MessageDialogOptionsButton> {
        const mixedOptions = { ...options };
        mixedOptions.messageType = 'warning';

        return this.showDialog(mixedOptions);
    }

    /** Returns the default ok button structure */
    getOkButtonConfig(): MessageDialogOptionsButton {
        return {
            id: 'ok',
            text: 'GENERAL.OK',
            primary: true,
        };
    }

    /** Returns the default cancel button structure */
    getCancelButtonConfig(): MessageDialogOptionsButton {
        return {
            id: 'cancel',
            text: 'GENERAL.CANCEL',
            primary: false,
        };
    }

    /** Returns the default yes button structure */
    getYesButtonConfig(): MessageDialogOptionsButton {
        return {
            id: 'yes',
            text: 'GENERAL.YES',
            primary: true,
        };
    }

    /** Returns the default no button structure */
    getNoButtonConfig(): MessageDialogOptionsButton {
        return {
            id: 'no',
            text: 'GENERAL.NO',
            primary: false,
        };
    }

    /** Opens an error modal dialog */
    showError(options: Partial<MessageDialogOptions>): Promise<MessageDialogOptionsButton> {
        const mixedOptions = { ...options };
        mixedOptions.messageType = 'error';

        return this.showDialog(mixedOptions);
    }

    private showDialog(options: Partial<MessageDialogOptions>): Promise<MessageDialogOptionsButton> {
        return new Promise((resolve: (value: any) => void) => {
            const mixedOptions = { ...this.defaultOptions, ...options };
            const popupRef: TwcPopupRef = this.popupService.open(MessageDialogComponent, { params: { options: mixedOptions } });

            this.buttonClicked$.pipe(first()).subscribe((id) => {
                popupRef.close();

                resolve(id);
            });
        });
    }
}
